// Fatia S1: instruções de exclusão de dados (exigida pela Meta e pelo Google) e página de status do pedido feito pela Meta.
import { useQuery } from '@tanstack/react-query'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../../lib/api'
import { LEGAL } from '../../features/public/legal'
import '../../features/public/site.css'

/** Status de um pedido de exclusão feito pela Meta (link devolvido a ela com o código de confirmação). */
function RequestStatus({ code }: { code: string }) {
  const q = useQuery({
    queryKey: ['data-deletion', code],
    queryFn: () => api.get<{ status: 'completed'; requestedAt: string }>(`/public/data-deletion/${encodeURIComponent(code)}`),
    retry: false,
  })
  return (
    <div className="card" role="status">
      <h2>Pedido de exclusão</h2>
      <p className="small muted">Código de confirmação: <b>{code}</b></p>
      {q.isLoading && <p>Consultando o pedido…</p>}
      {q.isError && <p>Não encontramos um pedido com esse código. Confira o código ou fale com a gente em <a href={`mailto:${LEGAL.privacyEmail}`}>{LEGAL.privacyEmail}</a>.</p>}
      {q.data && (
        <>
          <p><b>Concluído</b> em {new Date(q.data.requestedAt).toLocaleString('pt-BR')}.</p>
          <p>Desconectamos as contas ligadas a esse usuário da Meta e apagamos as autorizações (tokens) que guardávamos. Os resultados e as campanhas já importados pertencem à conta do negócio no {LEGAL.brand} e saem quando a conta é excluída (veja o item 4 abaixo).</p>
        </>
      )}
    </div>
  )
}

export default function DataDeletion() {
  const code = useSearchParams()[0].get('codigo')
  return (
    <article className="legal">
      <h1>Exclusão de dados</h1>
      <p className="small muted">Última atualização: {LEGAL.updatedAt}</p>
      {code && <RequestStatus code={code} />}
      <p>Você pode remover as conexões e pedir a exclusão dos seus dados a qualquer momento. Veja as opções:</p>

      <h2>1. Desconectar o Google, a Meta, o TikTok ou a OpenAI Ads (dentro do Seu Impulso)</h2>
      <ol>
        <li>Entre no painel e abra a empresa.</li>
        <li>Vá em <b>Contas conectadas</b>.</li>
        <li>Clique em <b>Desconectar</b> na conta desejada.</li>
      </ol>
      <p>Paramos de ler dados dessa conta na hora e pedimos à plataforma que revogue a autorização. Na OpenAI Ads a conexão usa uma chave de API e não há como revogá-la por aqui: apague a chave em <b>Ads Manager → Settings → API keys</b>. Quando nenhuma outra conta usa o mesmo acesso, o token guardado é apagado. Os resultados e as campanhas que já tínhamos importado continuam na sua conta do {LEGAL.brand} até você excluí-la (item 4). Enquanto a conta está conectada, guardamos no máximo 35 dias de dados por hora e 90 dias de termos de pesquisa; o que é mais antigo é apagado a cada atualização.</p>

      <h2>2. Remover o acesso pelo Facebook/Instagram</h2>
      <ol>
        <li>No Facebook, abra <b>Configurações e privacidade → Configurações</b>.</li>
        <li>Entre em <b>Aplicativos e sites</b> (ou “Integrações comerciais”).</li>
        <li>Encontre o <b>{LEGAL.brand}</b> e clique em <b>Remover</b>.</li>
      </ol>
      <p>A Meta avisa o {LEGAL.brand} da remoção. Quando você também marca a opção de apagar os dados, desconectamos as contas ligadas a você, apagamos as autorizações (tokens) que guardávamos e geramos um código de confirmação. Você consulta o andamento nesta página, pelo link que a Meta mostra com o código. Se você só remove o app, as conexões ficam marcadas como “precisa reconectar” e a autorização guardada é apagada.</p>

      <h2>3. Remover o acesso pelo Google</h2>
      <ol>
        <li>Acesse <a href="https://myaccount.google.com/permissions" target="_blank" rel="noreferrer">myaccount.google.com/permissions</a>.</li>
        <li>Selecione o <b>{LEGAL.brand}</b> e clique em <b>Remover acesso</b>.</li>
      </ol>

      <h2>4. Excluir a conta e todos os dados</h2>
      <p>Quem é dono da conta pode excluí-la sozinho em <b>Meu perfil e segurança → Excluir conta e dados</b>. Fazemos tudo na hora: cancelamos a assinatura, desconectamos as plataformas e apagamos cadastro, empresas, conexões e tokens, métricas, campanhas importadas, sugestões, textos gerados e dados de leads.</p>
      <p>Se preferir, envie um e-mail para <a href={`mailto:${LEGAL.privacyEmail}?subject=Exclus%C3%A3o%20de%20dados`}>{LEGAL.privacyEmail}</a> a partir do e-mail cadastrado, com o assunto “Exclusão de dados” e o nome da conta ou da empresa.</p>
      <ul>
        <li>Respondemos em até 2 dias úteis e concluímos a exclusão em até 15 dias.</li>
        <li>Guardamos apenas o que a lei obriga (por exemplo, notas fiscais e registros de acesso pelo prazo legal), sem uso para outra finalidade.</li>
        <li>Se a sua empresa é cliente de uma agência, o pedido pode ser feito também pela agência.</li>
      </ul>

      <p className="mt-24">Mais detalhes na <Link to="/privacidade">política de privacidade</Link>.</p>
    </article>
  )
}
